<template>
  <!-- Exercicios dos alunos para quem corrige (card 17trt6jcyfb). Serve a aba
       Exercicios da turma e a pagina do tutor (alunos Autonomy). O que pede
       acao vem primeiro: tentativa finalizada ainda nao revisada (retorno de
       16/10: sai da lista ao marcar como revisado ou comentar). -->
  <div class="sxo">
    <page-skeleton v-if="loading" variant="list" bare :rows="3" />

    <p v-else-if="error" class="sxo__state sxo__state--text">
      {{ $t("studentExercises.loadFailed") }}
    </p>

    <p v-else-if="!students.length" class="sxo__state sxo__state--text">
      {{ emptyText || $t("studentExercises.empty") }}
    </p>

    <template v-else>
      <p class="sxo__summary">
        <template v-if="awaitingTotal">
          <i18n
            path="studentExercises.awaiting"
            :plural="awaitingTotal"
            tag="span"
          >
            <template #count>
              <b>{{ awaitingTotal }}</b>
            </template>
          </i18n>
        </template>
        <template v-else>{{ $t("studentExercises.allReviewed") }}</template>
      </p>

      <div
        v-for="student in sortedStudents"
        :key="student.customerId"
        class="sxo__student"
      >
        <button
          type="button"
          class="sxo__row"
          :aria-expanded="String(openId === student.customerId)"
          @click="toggle(student)"
        >
          <span class="sxo__avatar" aria-hidden="true">{{
            initials(student.name)
          }}</span>
          <span class="sxo__who">
            <span class="sxo__name">{{ student.name }}</span>
            <span class="sxo__meta">
              {{
                $tc("studentExercises.finished", student.finished, {
                  count: student.finished,
                })
              }}
              <template v-if="student.inProgress">
                {{
                  $t("studentExercises.inProgress", {
                    count: student.inProgress,
                  })
                }}</template
              >
            </span>
          </span>
          <span v-if="student.awaitingReview" class="sxo__await">
            {{
              $t("studentExercises.notReviewed", {
                count: student.awaitingReview,
              })
            }}
          </span>
          <span v-if="student.percentage !== null" class="sxo__pct">
            {{ student.percentage }}%
          </span>
          <i
            class="mdi sxo__chev"
            :class="
              openId === student.customerId
                ? 'mdi-chevron-up'
                : 'mdi-chevron-down'
            "
            aria-hidden="true"
          ></i>
        </button>

        <div v-if="openId === student.customerId" class="sxo__attempts">
          <page-skeleton v-if="attemptsLoading" variant="rows" :rows="2" />
          <p v-else-if="!attempts.length" class="sxo__none">
            {{ $t("studentExercises.noneOpened") }}
          </p>
          <template v-else>
            <router-link
              v-for="attempt in attempts"
              :key="attempt.id"
              :to="`/connect/professor/exercicios/tentativas/${attempt.id}`"
              class="sxo__attempt"
            >
              <span class="sxo__set">
                {{ attempt.setTitle }}
                <span class="sxo__ex">{{
                  $t("studentExercises.exerciseN", {
                    n: attempt.exerciseOrder + 1,
                  })
                }}</span>
              </span>
              <span v-if="attempt.status !== 'FINISHED'" class="sxo__tag">
                {{ $t("studentExercises.inProgressTag") }}
              </span>
              <template v-else>
                <span v-if="!attempt.reviewedAt" class="sxo__tag sxo__tag--pen">
                  {{ $t("studentExercises.review") }}
                </span>
                <span class="sxo__score">
                  {{ attempt.correctItems }}/{{ attempt.totalItems }}
                </span>
              </template>
            </router-link>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";

export default {
  name: "StudentExerciseOverview",

  props: {
    // Turma; sem ela, a lista e a dos alunos Autonomy do tutor.
    groupId: { type: String, default: null },
    emptyText: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      students: [],
      loading: true,
      error: null,
      openId: null,
      attempts: [],
      attemptsLoading: false,
    };
  },

  computed: {
    awaitingTotal() {
      return this.students.reduce((sum, s) => sum + s.awaitingReview, 0);
    },
    sortedStudents() {
      return [...this.students].sort(
        (a, b) =>
          b.awaitingReview - a.awaitingReview ||
          String(a.name).localeCompare(String(b.name))
      );
    },
  },

  watch: {
    groupId() {
      this.load();
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      this.loading = true;
      this.error = null;
      const request = this.groupId
        ? partnerApi.getGroupExercises(this.groupId)
        : partnerApi.getTutoredExercises();
      request
        .then((res) => {
          this.students = res.data?.data || [];
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggle(student) {
      if (this.openId === student.customerId) {
        this.openId = null;
        return;
      }
      this.openId = student.customerId;
      this.attempts = [];
      this.attemptsLoading = true;
      partnerApi
        .getStudentExerciseAttempts(student.customerId)
        .then((res) => {
          const list = res.data?.data || [];
          // Nao revisadas primeiro; depois as mais recentes.
          this.attempts = list.sort(
            (a, b) =>
              Number(a.status !== "FINISHED" || !!a.reviewedAt) -
              Number(b.status !== "FINISHED" || !!b.reviewedAt)
          );
        })
        .catch(() => {
          this.attempts = [];
        })
        .finally(() => {
          this.attemptsLoading = false;
        });
    },

    initials(name) {
      return String(name || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");
    },
  },
};
</script>

<style lang="scss" scoped>
.sxo {
  --ink: var(--dk-ink, #2b2540);
  --muted: var(--dk-muted, #7a7590);
  --rule: var(--dk-line, #ece6f5);
  --fill: var(--dk-grape, #a826e1);
  /* Retorno de 16/10: a caneta do professor e roxa. */
  --pen: var(--dk-grape, #a826e1);
}

.sxo__state {
  display: flex;
  justify-content: center;
  padding: 28px 0;
}

.sxo__state--text {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
}

.sxo__summary {
  margin: 0 0 12px;
  font-size: 14.5px;
  color: var(--ink);
}

.sxo__summary b {
  color: var(--pen);
}

.sxo__student {
  border: 1px solid var(--rule);
  border-radius: 14px;
  background: var(--dk-surface, #fff);
  margin-bottom: 10px;
  overflow: hidden;
}

.sxo__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.sxo__row:focus-visible {
  outline: 3px solid rgba(168, 38, 225, 0.3);
  outline-offset: -3px;
}

.sxo__avatar {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f6ecfd;
  color: var(--fill);
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.sxo__who {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.sxo__name {
  font-weight: 600;
  color: var(--ink);
}

.sxo__meta {
  font-size: 12.5px;
  color: var(--muted);
}

.sxo__await {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: var(--pen);
  white-space: nowrap;
}

.sxo__pct {
  min-width: 44px;
  text-align: right;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: var(--ink);
}

.sxo__chev {
  color: var(--muted);
  font-size: 20px;
}

.sxo__attempts {
  padding: 4px 16px 10px 64px;
  border-top: 1px dashed var(--rule);
}

.sxo__none {
  margin: 10px 0;
  font-size: 13.5px;
  color: var(--muted);
}

.sxo__attempt {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px dashed var(--rule);
  text-decoration: none;
  color: var(--ink);
}

.sxo__attempt:first-of-type {
  border-top: 0;
}

.sxo__attempt:hover .sxo__set {
  color: var(--fill);
}

.sxo__attempt:focus-visible {
  outline: 3px solid rgba(168, 38, 225, 0.3);
  border-radius: 6px;
}

.sxo__set {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  transition: color 0.15s ease;
}

.sxo__ex {
  font-weight: 400;
  color: var(--muted);
}

.sxo__tag {
  font-size: 12px;
  color: var(--muted);
}

.sxo__tag--pen {
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 600;
  font-style: italic;
  color: var(--pen);
}

.sxo__score {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 16px;
}

@media (max-width: 600px) {
  .sxo__attempts {
    padding-left: 16px;
  }

  .sxo__await {
    display: none;
  }
}
</style>
